import { Lock } from "lucide-react";
import type { ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useCapabilityContext } from "@/providers/CapabilitiesProvider";
import { getLockedFeatureCopy } from "@/components/locked/featureCopy";

type LockedFeatureCardProps = {
  flag?: string;
  title?: string;
  description?: string;
  actionLabel?: string;
  featureId?: string;
  onUpgrade?: () => void;
  className?: string;
  children?: ReactNode;
  screenshot?: string;
};

export default function LockedFeatureCard({
  flag,
  title,
  description,
  actionLabel = "Upgrade to Pro",
  featureId,
  onUpgrade,
  className,
  children,
  screenshot,
}: LockedFeatureCardProps) {
  const { openUpgradeModal } = useCapabilityContext();
  const copy = flag ? getLockedFeatureCopy(flag) : undefined;
  const resolvedTitle = title ?? copy?.title ?? "Upgrade to unlock";
  const resolvedDescription =
    description ?? copy?.description ?? "This capability is part of the Pro toolkit with automation, CRM, and routing upgrades.";
  const resolvedFeature = featureId ?? copy?.featureName ?? flag ?? resolvedTitle;
  const resolvedScreenshot = screenshot ?? copy?.screenshot;

  const handleUpgrade = () => {
    onUpgrade?.();
    openUpgradeModal(resolvedFeature);
  };

  return (
    <div className={cn("rounded-2xl border border-primary/30 bg-background/50 p-4 text-sm text-foreground", className)}>
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full border border-amber-400/40 bg-amber-400/10">
          <Lock className="h-5 w-5 text-primary-foreground dark:text-amber-200" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-amber-300">Locked feature</p>
          <h3 className="text-lg font-semibold text-foreground">{resolvedTitle}</h3>
        </div>
      </div>
      <p className="mt-2 text-muted-foreground">{resolvedDescription}</p>
      {(resolvedScreenshot || children) && (
        <div className="mt-3 rounded-xl border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
          {children ?? <span>{resolvedScreenshot}</span>}
        </div>
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="mt-4 border-amber-300 text-primary-foreground dark:text-amber-100 hover:border-amber-200 hover:text-amber-50"
        onClick={handleUpgrade}
      >
        {actionLabel}
      </Button>
    </div>
  );
}
